<template>
  <!-- Нижняя панель редактора - тот же вид и жест, что у шторки деталей
       маршрута (MobileRouteDetailSheet.vue): карточный фон, скругление только
       сверху, мягкая тень, тонкая ручка, свободная высота между "виден только
       заголовок" и 90% экрана. На десктопе то же самое, но шириной 560px у
       правого края. Появляется свёрнутой - выглядывает заголовок
       "Статистика"; тап по шапке раскрывает/сворачивает, тянуть можно за
       ручку и шапку. -->
  <div
    class="pointer-events-auto fixed inset-x-0 md:left-auto md:right-16 md:w-[560px] bottom-[calc(59px+env(safe-area-inset-bottom,0px))] md:bottom-[66px] z-30 bg-sheet rounded-t-sheet border border-b-0 border-x-0 md:border-x md:border-b-0 border-border shadow-ink overflow-hidden flex flex-col"
    :class="!isDragging && 'transition-[height] duration-200 ease-out'"
    :style="{ height: `${panelHeight}px` }"
    @pointerdown="onPointerDown"
  >
    <!-- Ручка + шапка - область перетаскивания (как data-sheet-drag-handle) -->
    <div ref="headerEl" class="shrink-0 select-none cursor-ns-resize" style="touch-action: none">
      <div class="pt-1.5 pb-1 flex flex-col items-center">
        <div class="w-10 h-1 rounded-full bg-[hsl(var(--muted-foreground)/0.3)]" />
      </div>
      <div class="px-4 pb-2.5 flex items-center justify-between gap-2">
        <!-- Свёрнутая шапка - сводка маршрута в одну строку; пока линии
             нет, прежний заголовок. Вытянул панель - полная карточка. -->
        <div v-if="analytics" class="flex-1 flex items-center justify-between gap-x-3 gap-y-1 flex-wrap text-[12.5px] font-semibold tabular-nums min-w-0" data-editor-compact-stats>
          <span class="inline-flex items-center gap-1"><Ruler class="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" aria-hidden="true" />{{ analytics.distance_km.toFixed(1) }} {{ t('units.km') }}</span>
          <span class="text-[hsl(var(--muted-foreground))]">↑{{ formatOrDash(analytics.elevation_gain_m) }}</span>
          <span class="text-[hsl(var(--muted-foreground))]">↓{{ formatOrDash(analytics.elevation_loss_m) }}</span>
          <span v-if="analytics.elevation_profile" class="text-[hsl(var(--muted-foreground))]">▲{{ Math.round(analytics.elevation_profile.max_elevation_m) }}{{ t('units.m') }}</span>
          <span v-if="analytics.estimated_duration_minutes != null" class="inline-flex items-center gap-1 text-[hsl(var(--muted-foreground))]"><Clock class="w-3.5 h-3.5" aria-hidden="true" />{{ formatDuration(analytics.estimated_duration_minutes) }}</span>
        </div>
        <h3 v-else class="text-[14px] font-semibold">{{ t('map.routeEditor.tabStats') }}</h3>
        <ChevronUp class="shrink-0 w-4 h-4 text-[hsl(var(--muted-foreground))] transition-transform" :class="expanded && 'rotate-180'" />
      </div>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto border-t border-[hsl(var(--border)/0.5)]" data-sheet-no-drag>
      <div class="px-3 pt-3">
        <input
          :value="name"
          type="text"
          maxlength="200"
          class="w-full h-9 px-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[13px] font-semibold"
          :placeholder="t('map.routeEditor.namePlaceholder')"
          :aria-label="t('map.routeEditor.namePlaceholder')"
          data-editor-route-name
          @input="emit('update:name', ($event.target as HTMLInputElement).value)"
        />
      </div>
      <div v-if="!analytics" class="flex items-center justify-center h-24 text-[11px] text-[hsl(var(--muted-foreground))]">
        {{ t('map.routeEditor.needTwoPoints') }}
      </div>
      <div v-else class="p-3 space-y-3">
        <!-- Итоги -->
        <div>
          <div class="grid grid-cols-3 gap-2 mb-2">
            <div class="text-center p-2 rounded-md bg-[hsl(var(--muted)/0.4)]">
              <p class="text-[9px] font-semibold text-[hsl(var(--muted-foreground))]">{{ t('map.routeEditor.total') }}</p>
              <p class="text-[13px] font-semibold tabular-nums">{{ analytics.distance_km.toFixed(1) }} {{ t('units.km') }}</p>
            </div>
            <div class="text-center p-2 rounded-md bg-[hsl(var(--muted)/0.4)]">
              <p class="text-[9px] font-semibold text-[hsl(var(--muted-foreground))]">{{ t('map.routeEditor.gain') }}</p>
              <p class="text-[13px] font-semibold tabular-nums">{{ analytics.elevation_gain_m != null ? `↑${Math.round(analytics.elevation_gain_m)}` : '—' }}</p>
            </div>
            <div class="text-center p-2 rounded-md bg-[hsl(var(--muted)/0.4)]">
              <p class="text-[9px] font-semibold text-[hsl(var(--muted-foreground))]">{{ t('map.routeEditor.loss') }}</p>
              <p class="text-[13px] font-semibold tabular-nums">{{ analytics.elevation_loss_m != null ? `↓${Math.round(analytics.elevation_loss_m)}` : '—' }}</p>
            </div>
          </div>
          <p v-if="analytics.estimated_duration_minutes != null" class="text-[11px] text-[hsl(var(--muted-foreground))]">
            {{ t('map.routeEditor.duration') }}: <strong class="text-[hsl(var(--foreground))]">{{ formatDuration(analytics.estimated_duration_minutes) }}</strong>
          </p>
        </div>

        <!-- Профиль высот -->
        <div v-if="profileChart">
          <div class="flex items-center justify-between mb-1">
            <span class="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">{{ t('map.routeEditor.profileTitle') }}</span>
            <span class="text-[11px] font-semibold">{{ analytics.distance_km.toFixed(1) }} {{ t('units.km') }}</span>
          </div>
          <div class="flex items-stretch gap-1.5">
            <div class="relative shrink-0 h-24" style="width: 30px">
              <span
                v-for="tick in elevationTicks"
                :key="`y-${tick.value}`"
                class="absolute right-0 -translate-y-1/2 text-[9px] leading-none text-[hsl(var(--muted-foreground)/0.75)] whitespace-nowrap"
                :style="{ top: `${tick.pct}%` }"
              >{{ tick.label }}</span>
            </div>
            <svg class="flex-1 h-24 overflow-visible" viewBox="0 0 320 96" preserveAspectRatio="none">
              <defs>
                <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="hsl(158,38%,36%)" stop-opacity="0.4" />
                  <stop offset="100%" stop-color="hsl(158,38%,36%)" stop-opacity="0.06" />
                </linearGradient>
              </defs>
              <line v-for="tick in elevationTicks" :key="`gy-${tick.value}`" x1="0" :y1="tick.y" x2="320" :y2="tick.y" stroke="hsl(220,10%,44%)" stroke-opacity="0.15" stroke-width="0.6" />
              <path :d="profileChart.areaPath" :fill="`url(#${gradientId})`" />
              <path :d="profileChart.linePath" fill="none" stroke="hsl(158,38%,36%)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
              <g v-for="m in profileChart.markers" :key="`${m.kind}-${m.distance_km}`">
                <line :x1="m.x" :x2="m.x" y1="4" y2="88" :stroke="markerColor(m.kind)" stroke-opacity="0.25" stroke-width="1" stroke-dasharray="3 3" />
                <circle :cx="m.x" :cy="m.y" r="4" :fill="markerColor(m.kind)" stroke="white" stroke-width="1.5" />
              </g>
            </svg>
          </div>
          <div class="flex items-center gap-3 mt-1.5 text-[10px] text-[hsl(var(--muted-foreground))]">
            <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full" :style="{ background: markerColor('night-stop') }" />{{ t('poiTypes.night-stop') }}</span>
            <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full" :style="{ background: markerColor('rest-stop') }" />{{ t('poiTypes.rest-stop') }}</span>
          </div>
        </div>

        <!-- Дни -> участки. Всегда с протяжённостью дня, даже если день один -->
        <div v-if="analytics.daily_segments.length" class="space-y-1.5">
          <div v-for="day in analytics.daily_segments" :key="day.day_index" class="rounded-md border border-border overflow-hidden">
            <button type="button" class="w-full flex items-center justify-between gap-2 px-2.5 py-2 bg-[hsl(var(--muted)/0.35)]" @click="toggleDay(day.day_index)">
              <span class="text-[11.5px] font-semibold truncate">{{ day.label }}</span>
              <span class="flex items-center gap-1.5 shrink-0 text-[10.5px] font-semibold text-[hsl(var(--muted-foreground))]">
                {{ day.distance_km.toFixed(1) }} {{ t('units.km') }}
                <ChevronDown class="w-3 h-3 transition-transform" :class="!collapsedDays.has(day.day_index) && 'rotate-180'" />
              </span>
            </button>
            <div v-if="!collapsedDays.has(day.day_index)" class="p-2 space-y-1">
              <p class="text-[10.5px] text-[hsl(var(--muted-foreground))] px-0.5">{{ day.note }} · {{ day.distance_km.toFixed(1) }} {{ t('units.km') }} · ↑{{ formatOrDash(day.elevation_gain_m) }} ↓{{ formatOrDash(day.elevation_loss_m) }}</p>
              <div v-if="day.sections.length" class="space-y-1 mt-1">
                <div v-for="section in day.sections" :key="section.section_index" class="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-[hsl(var(--muted)/0.3)]">
                  <span class="text-[10.5px] font-semibold truncate">{{ section.note }}</span>
                  <span class="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">{{ section.distance_km.toFixed(1) }}{{ t('units.km') }} · ↑{{ formatOrDash(section.elevation_gain_m) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { formatHikingDuration } from "../utils/formatDuration";
import { useI18n } from "vue-i18n";
import { ChevronDown, ChevronUp, Clock, Ruler } from "lucide-vue-next";
import type { RoutePreviewAnalyticsResponse } from "../types/routes";

const props = defineProps<{
  analytics: RoutePreviewAnalyticsResponse | null;
  name: string;
}>();
const emit = defineEmits<{ (e: "update:name", value: string): void }>();

const { t } = useI18n();

const gradientId = `route-editor-fill-${Math.random().toString(36).slice(2)}`;

// Свободная высота (как в MobileRouteDetailSheet.vue): низ - ручка + шапка с
// заголовком "Статистика", верх - 90% экрана. Панель открывается свёрнутой.
const headerEl = ref<HTMLElement | null>(null);
// Реальная высота ручки+шапки (замер после монтирования); до него - близкое
// приближение, чтобы на первом кадре панель не схлопывалась в ничто.
const headerHeight = ref(44);
onMounted(() => {
  if (headerEl.value) headerHeight.value = headerEl.value.offsetHeight;
});
const getVH = () => (typeof window !== "undefined" ? window.innerHeight : 800);
const minHeight = () => headerHeight.value;
// 58px - нижний таб-бар на мобильном (панель стоит над ним), 0.9 - см. ту же
// оговорку про системный UI браузеров в MobileRouteDetailSheet.vue.
const maxHeight = () => Math.round(getVH() * 0.9 - 58);

// null - панель свёрнута (высота = только шапка); число - высота, выставленная
// пользователем/тапом.
const height = ref<number | null>(null);
const liveHeight = ref<number | null>(null);
const isDragging = ref(false);
const panelHeight = computed(() => liveHeight.value ?? height.value ?? minHeight());
const expanded = computed(() => panelHeight.value > minHeight() + 8);

let dragStartY = 0;
let dragStartHeight = 0;
let moved = false;
let dragging = false;

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

const onPointerMove = (e: PointerEvent) => {
  if (!dragging) return;
  const dy = e.clientY - dragStartY;
  if (!moved && Math.abs(dy) < 6) return;
  moved = true;
  isDragging.value = true;
  e.preventDefault();
  liveHeight.value = clamp(dragStartHeight - dy, minHeight(), maxHeight());
};

const onPointerUp = () => {
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  window.removeEventListener("pointercancel", onPointerUp);
  if (!dragging) return;
  dragging = false;
  if (moved && liveHeight.value != null) {
    const min = minHeight();
    const threshold = Math.max(56, (maxHeight() - min) * 0.08);
    // "Доводчик" только у нижней границы, как в шторке деталей маршрута.
    height.value = liveHeight.value - min < threshold ? null : liveHeight.value;
  } else {
    // Тап по ручке/шапке без перетаскивания - свернуть/раскрыть.
    height.value = expanded.value ? null : Math.round(getVH() * 0.5);
  }
  liveHeight.value = null;
  isDragging.value = false;
};

const onPointerDown = (e: PointerEvent) => {
  const target = e.target as HTMLElement;
  // Жест по телу контента - нативный скролл, ресайз только за ручку/шапку.
  if (target.closest("[data-sheet-no-drag]") || !headerEl.value?.contains(target)) return;
  dragging = true;
  moved = false;
  dragStartY = e.clientY;
  dragStartHeight = panelHeight.value;
  window.addEventListener("pointermove", onPointerMove, { passive: false });
  window.addEventListener("pointerup", onPointerUp);
  window.addEventListener("pointercancel", onPointerUp);
};

onBeforeUnmount(() => {
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  window.removeEventListener("pointercancel", onPointerUp);
});

const markerColor = (kind: string) => {
  if (kind === "start") return "hsl(155,55%,42%)";
  if (kind === "finish") return "hsl(222,25%,11%)";
  if (kind === "rest-stop") return "hsl(210,80%,55%)";
  return "hsl(38,92%,50%)"; // night-stop
};

const profileChart = computed(() => {
  const profile = props.analytics?.elevation_profile;
  if (!profile || !Array.isArray(profile.points) || profile.points.length < 2) return null;
  const W = 320, T = 6, B = 90;
  const totalDist = profile.distance_km || profile.points.at(-1)!.distance_km || 1;
  const minEl = profile.min_elevation_m;
  const range = Math.max(profile.max_elevation_m - minEl, 1);
  const tx = (d: number) => (Math.max(0, Math.min(d, totalDist)) / totalDist) * W;
  const ty = (e: number) => B - ((e - minEl) / range) * (B - T);
  const line = profile.points.map((p, i) => `${i === 0 ? "M" : "L"} ${tx(p.distance_km).toFixed(1)} ${ty(p.elevation_m).toFixed(1)}`).join(" ");
  return {
    linePath: line,
    areaPath: `${line} L ${W} ${B} L 0 ${B} Z`,
    markers: (profile.markers || [])
      .filter((m) => m.kind === "night-stop" || m.kind === "rest-stop")
      .map((m) => ({ ...m, x: tx(m.distance_km), y: ty(typeof m.elevation_m === "number" ? m.elevation_m : minEl) })),
  };
});

const niceStep = (range: number, targetCount: number): number => {
  const rough = range / Math.max(targetCount, 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough || 1));
  const residual = rough / magnitude;
  if (residual > 5) return 10 * magnitude;
  if (residual > 2) return 5 * magnitude;
  if (residual > 1) return 2 * magnitude;
  return magnitude;
};

const elevationTicks = computed(() => {
  const profile = props.analytics?.elevation_profile;
  if (!profile) return [];
  const T = 6, B = 90;
  const minEl = profile.min_elevation_m;
  const maxEl = profile.max_elevation_m;
  const range = Math.max(maxEl - minEl, 1);
  const step = niceStep(range, 4);
  const ticks: { value: number; y: number; pct: number; label: string }[] = [];
  for (let v = Math.ceil(minEl / step) * step; v <= maxEl + 1e-6; v += step) {
    const y = B - ((v - minEl) / range) * (B - T);
    ticks.push({ value: v, y, pct: (y / 96) * 100, label: `${Math.round(v)}${t("units.m")}` });
  }
  return ticks;
});

// Дни по умолчанию раскрыты - в них участки и протяжённость.
const collapsedDays = ref<Set<number>>(new Set());
const toggleDay = (dayIndex: number) => {
  const next = new Set(collapsedDays.value);
  if (next.has(dayIndex)) next.delete(dayIndex);
  else next.add(dayIndex);
  collapsedDays.value = next;
};

const formatOrDash = (value: number | null | undefined) => (value != null ? `${Math.round(value)}${t("units.m")}` : "—");

const formatDuration = (minutes: number): string =>
  formatHikingDuration(minutes, t("units.h"), t("units.decimal")) ?? "—";
</script>
